import { useEffect, useRef, useState } from 'react';
import { Shield, Loader2, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { startSsoReassertion } from '@/lib/ssoReassertion';

export interface StepUpAal2DialogState {
  open: boolean;
  reason: string;
  /**
   * Which step-up to render (ENT-4204). `'totp'` (default) collects a 6-digit
   * code; `'sso'` shows the re-authenticate-with-your-IdP CTA and performs the
   * `signInWithSSO` redirect itself.
   */
  mode?: 'totp' | 'sso';
  /** Resolved with the entered code, or null if the user cancels. */
  resolve: (code: string | null) => void;
}

interface StepUpAal2DialogProps {
  state: StepUpAal2DialogState | null;
  /**
   * Injectable for tests — starts the SSO re-assertion redirect. Defaults to the
   * shared `startSsoReassertion` helper.
   */
  startSsoReassertionImpl?: typeof startSsoReassertion;
}

/**
 * Modal that drives a step-up when the backend rejects an action. In `'totp'`
 * mode it prompts for a fresh TOTP code (`mfa_recent_required`); in `'sso'` mode
 * (`sso_reassertion_required`, ENT-4204) it offers a "Continue with single
 * sign-on" CTA that re-runs the IdP login and returns the user to their route.
 * Resolves the caller-supplied promise with the entered 6-digit code, or `null`
 * if cancelled.
 */
export function StepUpAal2Dialog({
  state,
  startSsoReassertionImpl = startSsoReassertion,
}: StepUpAal2DialogProps) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  // Track resolver identity, NOT reason. Two queued prompts with the
  // same `stepUpReason` (e.g. concurrent billing actions) would
  // otherwise share `lastReasonRef`, skip the reset, and leave
  // `submitting=true` from the first prompt locked on the second.
  // Codex round-3 P1 fix.
  const lastResolveRef = useRef<((code: string | null) => void) | null>(null);

  useEffect(() => {
    if (state?.open && state.resolve !== lastResolveRef.current) {
      // Fresh prompt — reset code + submitting + error state.
      setCode('');
      setSubmitting(false);
      setSsoError(null);
      lastResolveRef.current = state.resolve;
    }
    if (!state?.open) {
      lastResolveRef.current = null;
    }
  }, [state?.open, state?.resolve]);

  if (!state) return null;

  const isSso = state.mode === 'sso';

  const handleConfirm = () => {
    if (code.length !== 6) return;
    setSubmitting(true);
    state.resolve(code);
  };

  const handleCancel = () => {
    state.resolve(null);
  };

  const handleSsoReassert = async () => {
    setSubmitting(true);
    setSsoError(null);
    const result = await startSsoReassertionImpl();
    if (!result.redirected) {
      // Redirect did not start — surface the error and let the user retry/cancel.
      setSubmitting(false);
      setSsoError(result.error ?? 'Unable to start single sign-on. Please try again.');
    }
    // On success the browser navigates to the IdP; nothing else to do here.
  };

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
    >
      <DialogContent className="max-w-sm">
        {isSso ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-primary" />
                Confirm it's you
              </DialogTitle>
              <DialogDescription>
                Re-authenticate with your identity provider to continue.
              </DialogDescription>
            </DialogHeader>
            {ssoError && (
              <p className="text-sm text-destructive py-2" role="alert">
                {ssoError}
              </p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSsoReassert} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Continue with single sign-on
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Verify two-factor code
              </DialogTitle>
              <DialogDescription>
                {state.reason} requires a fresh two-factor code. Enter the 6-digit code from your authenticator app to continue.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-4">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => setCode(value)}
                disabled={submitting}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={submitting || code.length !== 6}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Verify
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
